import { RefObject, useEffect, useMemo, useState } from 'react';
// `Map` aliased to `GoogleMap` — the bare name would shadow the global `Map`
// constructor used for the tooltip lookup below.
import { APIProvider, Map as GoogleMap, useMap } from '@vis.gl/react-google-maps';
import { Box } from '@mui/material';

import { GOOGLE_API_KEY, GOOGLE_MAPS_MAP_ID_ADMI_MAP } from 'src/appConfig';
import { DeckGlOverlay } from 'src/common/components/AdmiMap';
import { BuildingsDeckLayer } from 'src/features/real-estate/BuildingsDeckLayer';
import { Building } from 'src/features/real-estate/buildings.dto';
import { STECKBRIEF_PHOTO_ASPECT } from './steckbrief-photo';

/**
 * The map as the Gebäudefoto camera (GEN-778) — driven by the host's
 * "Save map image" button (SteckbriefBuildingMapPicker).
 */
export interface SteckbriefMapCamera {
    /** The element the button photographs — attached to the map container. */
    containerRef: RefObject<HTMLDivElement | null>;
    /** True while the photo is taken: only the selected buildings, no hover, no zoom control. */
    capturing: boolean;
}

export interface SteckbriefBuildingPickerMapProps {
    /** Buildings to draw — only entries with a geometry; the container filters. */
    buildings: Building[];
    /** Ids of the buildings currently included in the Steckbrief. */
    selectedIds: Set<string>;
    /** Toggle a building in/out of the selection (polygon click). */
    onToggle: (buildingId: string) => void;
    /** Label shown as the polygon tooltip — the "(1/3) — address" building label. */
    buildingLabel: (buildingId: string) => string;
    /** Blocks toggling while the create request is in flight. */
    disabled?: boolean;
    /** Makes the map the Gebäudefoto camera (GEN-778): framed at the photo aspect, capture mode. */
    camera?: SteckbriefMapCamera;
}

/**
 * Compute the bounding box over every polygon vertex of the given buildings.
 * Pure lat/lng math (no `google.*` at runtime) so it stays unit-testable.
 */
function computeBounds(buildings: Building[]): google.maps.LatLngBoundsLiteral | null {
    let north = -Infinity;
    let south = Infinity;
    let east = -Infinity;
    let west = Infinity;
    let found = false;
    for (const building of buildings) {
        if (building.geometry == null) continue;
        const positions =
            building.geometry.type === 'Polygon'
                ? building.geometry.coordinates.flat()
                : building.geometry.coordinates.flat(2);
        for (const pos of positions) {
            // Defensive: a non-polygon geometry type or malformed coordinates would
            // otherwise throw on destructuring (and NaN would poison the bounds).
            if (!Array.isArray(pos) || pos.length < 2) continue;
            const [lng, lat] = pos;
            if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
            found = true;
            north = Math.max(north, lat);
            south = Math.min(south, lat);
            east = Math.max(east, lng);
            west = Math.min(west, lng);
        }
    }
    return found ? { north, south, east, west } : null;
}

/**
 * Fits the map camera to the given bounds once the map is ready.
 *
 * Gated on the map's first `idle` for the same reason as `MapPanController`
 * in AdmiMap: `useMap()` hands back the instance before the initial camera
 * has settled, and a `fitBounds` issued in that window is overridden when the
 * uncontrolled `defaultCenter`/`defaultZoom` are applied.
 */
function FitBounds({ bounds }: { bounds: google.maps.LatLngBoundsLiteral | null }) {
    const map = useMap();
    const [mapReady, setMapReady] = useState(false);

    useEffect(() => {
        if (!map) return;
        const listener = map.addListener('idle', () => setMapReady(true));
        return () => {
            listener.remove();
            setMapReady(false);
        };
    }, [map]);

    useEffect(() => {
        if (map && mapReady && bounds) {
            map.fitBounds(bounds, 32);
        }
    }, [map, mapReady, bounds]);
    return null;
}

/**
 * The map half of the Steckbrief building picker (GEN-700): a closed satellite
 * map showing ONLY the REA's building footprints — no search bar, no
 * surrounding buildings — where clicking a polygon toggles that building in
 * or out of the Steckbrief. Rendering reuses the REA page's own map pieces
 * (`DeckGlOverlay` + `BuildingsDeckLayer`), so selected buildings look the
 * same here as on the page map: red fill when selected, yellow otherwise,
 * opaque on hover, tooltip with the "(n/m) — address" label.
 *
 * With `camera` (GEN-778) the map doubles as the Gebäudefoto camera: the
 * container is framed at the print's photo aspect (259.8:166), so what the
 * user frames is what prints, and the host's "Save map image" (the PV
 * Calculator's shared MapSnapshotButton) photographs it — while `capturing`,
 * only the SELECTED buildings are drawn and the zoom control is hidden. The map
 * renders RASTER (like the PV map, #690/#691): html2canvas cannot read the
 * WebGL canvas of a VECTOR map. Tilt and rotation are locked to the top-down
 * view the photo needs.
 */
export function SteckbriefBuildingPickerMap({
    buildings,
    selectedIds,
    onToggle,
    buildingLabel,
    disabled = false,
    camera,
}: SteckbriefBuildingPickerMapProps) {
    const [hoveredBuildingId, setHoveredBuildingId] = useState<string | null>(null);
    const capturing = camera?.capturing ?? false;

    const selectedBuildings = useMemo(
        () => buildings.filter((building) => selectedIds.has(building.id)),
        [buildings, selectedIds],
    );

    const tooltipMap = useMemo(
        () => new Map(buildings.map((building) => [building.id, buildingLabel(building.id)])),
        [buildings, buildingLabel],
    );

    const bounds = useMemo(() => computeBounds(buildings), [buildings]);

    const layer = BuildingsDeckLayer(
        // The photo shows only the selected buildings (like the PV capture).
        capturing ? selectedBuildings : buildings,
        undefined,
        selectedBuildings,
        (clicked) => {
            if (!disabled && !capturing) onToggle(clicked.id);
        },
        undefined,
        capturing ? null : hoveredBuildingId,
        (newId) => setHoveredBuildingId((prev) => (prev === newId ? prev : newId)),
        tooltipMap,
    );

    const defaultCenter = bounds
        ? { lat: (bounds.north + bounds.south) / 2, lng: (bounds.east + bounds.west) / 2 }
        : undefined;

    return (
        <APIProvider apiKey={GOOGLE_API_KEY}>
            <Box
                ref={camera?.containerRef}
                sx={camera ? { width: '100%', aspectRatio: STECKBRIEF_PHOTO_ASPECT } : { width: '100%', height: 400 }}
            >
                <GoogleMap
                    mapId={GOOGLE_MAPS_MAP_ID_ADMI_MAP}
                    mapTypeId="satellite"
                    mapTypeControl={false}
                    streetViewControl={false}
                    fullscreenControl={false}
                    rotateControl={false}
                    cameraControl={false}
                    keyboardShortcuts={false}
                    // Hidden while the photo is taken — it would print.
                    zoomControl={!capturing}
                    style={{ width: '100%', height: '100%' }}
                    defaultCenter={defaultCenter}
                    defaultZoom={17}
                    defaultTilt={0}
                    tiltInteractionEnabled={false}
                    headingInteractionEnabled={false}
                    renderingType="RASTER"
                    draggableCursor={hoveredBuildingId && !capturing ? 'pointer' : undefined}
                >
                    <FitBounds bounds={bounds} />
                    <DeckGlOverlay layers={[layer]} />
                </GoogleMap>
            </Box>
        </APIProvider>
    );
}
